import React from 'react';
import { BodyTextStyle, Caption, Subtitle, mq } from '@kvdbil/components';
import styled from 'styled-components';
import { LeasingPromotionCardFragment } from '~/config/generated/graphql';
import { formatMoneyWithoutCurrency } from '~/helpers/currency';
import { useTranslation } from '~/Locale';

const IMAGE_ASPECT_RATIO = '244 / 137';
const DESKTOP_IMAGE_WIDTH = '10.25rem';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background.gray};
  color: ${({ theme }) => theme.colors.text.dark};

  ${mq('tablet')} {
    flex-direction: row;
  }
`;

const ProductImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  object-fit: cover;

  ${mq('tablet')} {
    flex-shrink: 0;
    width: ${DESKTOP_IMAGE_WIDTH};
    height: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding: 1rem;

  ${mq('tablet')} {
    padding: 0.5rem 1rem;
  }
`;

const BrandAndModel = styled(Subtitle)`
  && {
    ${mq('tablet')} {
      ${BodyTextStyle};
      font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
    }
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Value = styled(Caption)`
  && {
    text-align: right;
  }
`;

const BoldValue = styled(Value)`
  && {
    font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  }
`;

const Footnote = styled(Caption)`
  && {
    font-size: 0.625rem;
    line-height: 1rem;
  }
`;

type Props = Pick<
  LeasingPromotionCardFragment,
  | 'productImage'
  | 'brandAndModel'
  | 'monthlyCostFrom'
  | 'leasingPeriodFrom'
  | 'mileageFrom'
> & {
  priceFootnote?: string;
};

const LeasingPromotionSummary = ({
  productImage,
  brandAndModel,
  monthlyCostFrom,
  leasingPeriodFrom,
  mileageFrom,
  priceFootnote
}: Props) => {
  const { t } = useTranslation();

  const image = productImage?.responsiveImage;
  const footnoteMarker = priceFootnote ? '*' : '';

  return (
    <Wrapper>
      <Container>
        {image && (
          <ProductImage
            src={image.src ?? undefined}
            srcSet={image.srcSet ?? undefined}
            sizes={image.sizes ?? undefined}
            alt={image.alt ?? brandAndModel ?? ''}
          />
        )}

        <Content>
          <BrandAndModel as="h3">{brandAndModel}</BrandAndModel>

          <div>
            <Row>
              <Caption as="span">{t('Cost')}</Caption>
              {monthlyCostFrom != null && (
                <BoldValue as="span">
                  {t(
                    'fr. %s kr/month',
                    formatMoneyWithoutCurrency(monthlyCostFrom)
                  )}
                  {footnoteMarker}
                </BoldValue>
              )}
            </Row>

            <Row>
              <Caption as="span">
                {leasingPeriodFrom != null &&
                  t('fr. %s months', leasingPeriodFrom)}
              </Caption>
              <Value as="span">
                {mileageFrom != null &&
                  t('fr. %s mil/year', formatMoneyWithoutCurrency(mileageFrom))}
              </Value>
            </Row>
          </div>
        </Content>
      </Container>

      {priceFootnote && <Footnote>* {priceFootnote}</Footnote>}
    </Wrapper>
  );
};

export default LeasingPromotionSummary;
